import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { AdminIcon } from '@/components/admin/icon';
import { ITEM_PERMISSIONS } from '@/config/nav-permissions';
import { ADMIN_NAV, ADMIN_USER } from '@/data/admin-nav';
import { useAuth } from '@/hooks/use-auth';
import { useNotificationLogs } from '@/hooks/use-notification-logs';
import { useSidebarCounters } from '@/hooks/use-sidebar-counters';
import { usePermissions } from '@/hooks/use-permissions';
import { cn } from '@/lib/cn';

const formatCount = (n: number): string => (n > 999 ? Math.round(n / 100) / 10 + 'k' : String(n));

export function AdminSidebar() {
  const { user } = useAuth();
  const { has, isSuperAdmin } = usePermissions();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { failedCount } = useNotificationLogs();
  const counters = useSidebarCounters();

  return (
    <aside className="sidebar">
      <div className="brand">
        <img src="/logos/Logo-texte-blanc.svg" alt="Akwaba" style={{ height: 28, width: 'auto' }} />
        <div className="brand-label">Admin</div>
      </div>

      <nav className="nav">
        {ADMIN_NAV.map((group) => {
          const visible = group.items.filter((it) => {
            const required = ITEM_PERMISSIONS[it.k];
            return !required || isSuperAdmin || required.some((permission) => has(permission));
          });
          if (visible.length === 0) return null;
          return (
            <div key={group.group} className="nav-group">
              <div className="nav-group-h">{group.group}</div>
              {visible.map((rawItem) => {
                const it =
                  rawItem.k === 'notifications' && failedCount > 0
                    ? { ...rawItem, count: failedCount, alert: true }
                    : counters[rawItem.k] !== undefined
                      ? { ...rawItem, count: counters[rawItem.k] }
                      : rawItem;
                return (
                  <NavLink
                    key={it.k}
                    to={it.path}
                    end={it.path === '/'}
                    className={({ isActive }) =>
                      cn(
                        'nav-link',
                        (isActive || it.also?.some((p) => pathname.startsWith(p))) && 'active',
                      )
                    }
                  >
                    <span className="nav-icn">
                      <AdminIcon n={it.icn} s={16} c="currentColor" />
                    </span>
                    <span>{it.l}</span>
                    {it.count !== undefined && (
                      <span className={cn('count tn', it.alert && 'alert')}>
                        {formatCount(it.count)}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      <button
        type="button"
        className="sidebar-foot"
        style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%' }}
        onClick={() => navigate('/profil')}
        data-testid="sidebar-profile-link"
      >
        <div className="av" style={{ background: 'var(--a-ochre)', color: 'var(--a-ink)' }}>
          {user ? initials(user.fullName) : ADMIN_USER.initials}
        </div>
        <div className="who">
          <div className="name">{user?.fullName ?? ADMIN_USER.name}</div>
          <div className="role">{user?.role.label ?? ADMIN_USER.role}</div>
        </div>
        <span className="menu">
          <AdminIcon n="chevR" s={14} />
        </span>
      </button>
    </aside>
  );
}

function initials(fullName: string): string {
  return fullName
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
